<template>
    <pendo-multiselect
        v-if="hasMultipleApps"
        v-model="selectedOption"
        class="multiselect-filter-apps"
        :labels="labelsConfig"
        :append-to-body="appendToBody"
        :disabled="disabled || hasErrored"
        :trigger-size="triggerSize"
        :group-label-key="groupLabelKey"
        :group-options-key="groupOptionKey"
        :internal-search="internalSearch"
        :invalid="invalid"
        label-key="displayName"
        :loading="isLoadingOptions || loading"
        :options="options"
        :placeholder="localizedPlaceholder"
        :searchable="searchable"
        :max-trigger-width="maxTriggerWidth"
        :full-width="fullWidth"
        :popper-options="popperOptions"
        value-key="id"
        @open="onOpen">
        <template
            #trigger
            v-if="useDataMeasureTrigger">
            <pendo-data-measure-trigger />
        </template>
        <template #header>
            <slot name="header" />
        </template>
        <template #option="{ option }">
            <div class="option-wrapper">
                <pendo-app-display
                    :apps="option"
                    class="app-name" />
            </div>
        </template>
        <template #selectedLabel="{ option }">
            <pendo-app-display
                :apps="option"
                class="app-name" />
        </template>
    </pendo-multiselect>
</template>

<script>
/**
 * NOTE: This component should be used in siutations where the app chooser does not
 * need to modify the global selectedApp in Vuex. This component simply emits an event
 * when a new app is selected. The parent component can listen for that event and act
 * accordingly.
 **/

import find from 'lodash/find';
import get from 'lodash/get';
import remove from 'lodash/remove';
import { AppChooserService } from '@/common/app-chooser/app-chooser.service';
import { mapGetters, mapActions } from 'vuex';
import { PendoMultiselect, PendoAppDisplay, PendoDataMeasureTrigger } from '@pendo/components';
import { isMobileApp } from '@/utils/apps';

export default {
    name: 'LocalAppChooser',
    components: {
        PendoMultiselect,
        PendoAppDisplay,
        PendoDataMeasureTrigger,
    },
    props: {
        labelsConfig: {
            type: Object,
            default: () => ({}),
        },
        searchable: {
            type: Boolean,
            default: () => undefined,
        },
        internalSearch: {
            type: Boolean,
            default: () => undefined,
        },
        invalid: {
            type: Boolean,
            default: false,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        loading: {
            type: Boolean,
            default: false,
        },
        value: {
            type: [String, Number],
            default: undefined,
        },
        excludeGroups: {
            type: Boolean,
            default: false,
        },
        platform: {
            type: [String, Array],
            default: undefined,
        },
        enabledMap: {
            type: Object,
            default: () => ({}),
        },
        filterNonIntegratedApps: {
            type: Boolean,
            default: false,
        },
        placeholder: {
            type: String,
            default: undefined,
        },
        appendToBody: {
            type: Boolean,
            default: false,
        },
        removeRestrictedApps: {
            type: Boolean,
            default: false,
        },
        disabledOptionsMap: {
            type: Object,
            default: undefined,
        },
        optionsOverride: {
            type: Array,
            default: undefined,
        },
        popperOptions: {
            type: Object,
            default: undefined,
        },
        maxTriggerWidth: {
            type: Number,
            default: undefined,
        },
        triggerSize: {
            type: String,
            default: undefined,
        },
        useDataMeasureTrigger: {
            type: Boolean,
            default: false,
        },
        fullWidth: {
            type: Boolean,
            default: false,
        },
        filterNonMobileApps: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['input', 'loadError', 'open'],
    data () {
        return {
            isLoadingOptions: true,
            hasErrored: false,
            options: [],
        };
    },
    computed: {
        ...mapGetters({
            isRestrictedMobileApp: 'apps/isRestrictedMobileApp',
            isMultiApp: 'apps/isMultiApp',
        }),
        groupLabelKey () {
            return this.excludeGroups ? '' : 'group';
        },
        groupOptionKey () {
            return this.excludeGroups ? '' : 'apps';
        },
        hasMultipleApps () {
            if (this.optionsOverride) {
                return true;
            }

            return this.isMultiApp;
        },
        localizedPlaceholder () {
            return this.placeholder || this.$t('localAppChooser.localAppChooserPlaceholder');
        },
        selectedOption: {
            get () {
                if (!this.value) return '';
                if (this.optionsOverride) {
                    const indexForApp = this.optionsOverride.length - 1;

                    return find(this.optionsOverride[indexForApp].apps, (app) => app.id === this.value);
                }

                const option = AppChooserService.getOptionByAppId(this.value);
                delete option.icon;

                return option;
            },
            set (selectedOption) {
                if (selectedOption) {
                    this.$emit('input', selectedOption.id);
                }
            },
        },
    },
    watch: {
        enabledMap () {
            this.loadOptions();
        },
        disabledOptionsMap () {
            this.loadOptions();
        },
    },
    async created () {
        try {
            await this.fetchApps();
            this.selectedOption = await AppChooserService.loadPredefinedAppOption(this.selectedOption);
            this.loadOptions();
        } catch (error) {
            this.$emit('loadError', error);
            this.hasErrored = true;
        }

        this.isLoadingOptions = false;
    },
    methods: {
        ...mapActions('apps', ['loadAll']),
        fetchApps () {
            return this.loadAll();
        },
        loadOptions () {
            // optionsOverride is needed for a specific use case on the /setup page where the appchooser is used.
            // In this scenario, the page may be accessed publically, so we won't be able to rely on the internal
            // functions in the app chooser that fetch apps and format the multiselect options. optionsOverride
            // represents passed in options that are used instead of the internally fetched app options
            this.options = this.optionsOverride
                ? this.optionsOverride
                : AppChooserService.formatOptions(
                    this.platform,
                    this.excludeGroups,
                    this.enabledMap,
                    this.filterNonIntegratedApps,
                );

            // Fix what gets returned from AppChooserService to be compatible with pendo-multiselect.
            this.options.forEach((group) => {
                group.apps = group.apps.reduce((options, option) => {
                    // The old dropdown would filter out disabled items.
                    if (!option.$isDisabled) {
                        delete option.$isDisabled;
                        // Remove the icon.
                        delete option.icon;

                        if (!(this.filterNonMobileApps && isMobileApp(option?.platform))) {
                            options.push(option);
                        }
                    }
                    option.disabled = get(this.disabledOptionsMap, `${option.id}`, false);

                    return options;
                }, []);
            });

            if (this.removeRestrictedApps) {
                const appsSelector = `[${this.excludeGroups ? '0' : '1'}].apps`;
                remove(get(this.options, appsSelector), (app) => this.isRestrictedMobileApp(app.id));
            }

            if (this.excludeGroups) {
                this.options = this.options[0].apps;
            }
        },
        onOpen () {
            this.$emit('open');
        },
    },
};
</script>
<style scoped lang="less">
.segment-chooser-option__aside {
    color: var(--palette-gray-60);
    width: 100px;
    margin-left: 15px;
}

.app-name {
    width: 100%;
    &.nonIntegrated {
        width: 70%;
    }
}

.option-wrapper {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
}
</style>
